/**
 * Ergebnisquelle eines Events: von Hand oder aus RACE RESULT.
 *
 * Die Regie entscheidet pro Event, woher die Zieleinlaeufe kommen. `manual`
 * ist der bisherige Weg -- jemand tippt mit. `raceresult` laesst stattdessen
 * den *Server* in festem Takt die Ergebnisliste des Veranstalters abfragen
 * und schreibt daraus dieselben Streckeneintraege, die sonst von Hand
 * entstehen. Alles dahinter -- Ticker-Feed, Einblendungen, Zuschauerbild --
 * merkt keinen Unterschied.
 *
 * Warum auf dem Server und nicht im Regie-Browser:
 *
 * - Der API-Schluessel gehoert dem Veranstalter. Im Browser laege er im
 *   Speicher jedes Regie-Tabs und ginge ueber jede Entwicklerkonsole mit.
 * - Ein F5 im Regie-Tab wuerde die Abfrage abreissen lassen. Genau wie beim
 *   RTMP-Ausgang gilt: was weiterlaufen soll, laeuft oben.
 *
 * Abgefragt wird die „Simple API" von RACE RESULT: der Veranstalter legt
 * dort einen Schluessel fuer *eine* Liste an und bekommt eine Adresse wie
 * `https://api.raceresult.com/380756/B1AMZ…` -- Event-ID und Schluessel,
 * mehr nicht. Welche Liste zurueckkommt, steckt im Schluessel.
 */

/* ------------------------------------------------------------- Quelle */

export type ResultsSource = 'manual' | 'raceresult';

export const RESULTS_SOURCES: { id: ResultsSource; label: string; hint: string }[] = [
  { id: 'manual', label: 'Von Hand', hint: 'Die Regie trägt Zeiten selbst ein — wie bisher.' },
  {
    id: 'raceresult',
    label: 'RACE RESULT',
    hint: 'Der Server holt die Zeitnahme automatisch — Adresse der Simple API vom Veranstalter.',
  },
];

/** Was aus der Liste uebernommen werden soll. */
export interface ResultsImports {
  /** Teilnehmer, die es noch nicht gibt, in die Startliste aufnehmen. */
  startlist: boolean;
  /** Zwischenzeiten an Messpunkten. */
  splits: boolean;
  /** Zieleinlaeufe mit Zeit und Platz. */
  finishes: boolean;
}

export const defaultImports = (): ResultsImports => ({
  startlist: true,
  splits: true,
  finishes: true,
});

/**
 * Spaltenzuordnung.
 *
 * Wie eine Ergebnisliste aussieht, entscheidet der Veranstalter in seinem
 * RACE RESULT -- Spaltennamen, Reihenfolge und Sprache sind frei. Deshalb
 * raet die Automatik zuerst anhand der Ueberschriften; was sie falsch raet,
 * stellt die Regie hier gerade. Leer heisst „automatisch".
 *
 * Eintragen laesst sich der Spaltenname (`Zielzeit`) oder die Spaltennummer
 * ab 1 (`4`) -- je nachdem, was die Testabfrage zeigt.
 */
export interface ResultsFieldMap {
  bib: string;
  name: string;
  time: string;
  place: string;
  status: string;
  checkpoint: string;
  laps: string;
  /** Durchschnittliche Rundenzeit, z. B. „Runde ⌀" -- fuer die Streckenkarte. */
  lapTime: string;
}

export const emptyFieldMap = (): ResultsFieldMap => ({
  bib: '',
  name: '',
  time: '',
  place: '',
  status: '',
  checkpoint: '',
  laps: '',
  lapTime: '',
});

export const RESULTS_FIELDS: { id: keyof ResultsFieldMap; label: string; hint: string }[] = [
  { id: 'bib', label: 'Startnummer', hint: 'Bib, Startnr., Nr.' },
  { id: 'name', label: 'Name', hint: 'Name oder Vorname + Nachname' },
  { id: 'time', label: 'Zeit', hint: 'Zielzeit oder Zeit am Messpunkt' },
  { id: 'place', label: 'Platz', hint: 'Rang im Ziel' },
  { id: 'status', label: 'Status', hint: 'DNF, DNS, DSQ — sonst leer' },
  { id: 'checkpoint', label: 'Messpunkt', hint: 'Abschnitt der Zwischenzeit' },
  { id: 'laps', label: 'Runden', hint: 'Geschaffte Runden, z. B. beim Backyard' },
  { id: 'lapTime', label: 'Runde ⌀', hint: 'Durchschnittliche Rundenzeit — für die Streckenkarte' },
];

/* ------------------------------------------------------- Konfiguration */

export const MIN_RESULTS_INTERVAL = 10;
export const MAX_RESULTS_INTERVAL = 600;
export const DEFAULT_RESULTS_INTERVAL = 20;

/**
 * Liest Event-ID und Schluessel aus einer eingefuegten Adresse der Simple
 * API, `https://api.raceresult.com/380756/B1AMZ…`. RACE RESULT zeigt beim
 * Anlegen des Schluessels genau diese Adresse -- sie ganz zu kopieren ist
 * einfacher und sicherer, als sie von Hand auf zwei Felder zu verteilen.
 * Null, wenn der Text keine solche Adresse ist, etwa ein nackter Schluessel.
 */
export function parseResultsAddress(text: string): { eventKey: string; apiKey: string } | null {
  const match = /^\s*(?:https?:\/\/)?[^/\s]+\/(\d+)\/([A-Za-z0-9]+)\/?(?:[?#]\S*)?\s*$/.exec(text);
  return match ? { eventKey: match[1], apiKey: match[2] } : null;
}

/**
 * Was die Regie eingibt. Der API-Schluessel geht genau einmal hoch und kommt
 * nie zurueck -- nach aussen gibt es nur `keyHint`.
 */
export interface ResultsConfigInput {
  source: ResultsSource;
  /** Event-ID in RACE RESULT, z. B. `380756`. */
  eventKey: string;
  /** Schluessel der Simple API -- oder die ganze Adresse, siehe `parseResultsAddress`. */
  apiKey: string;
  intervalSeconds: number;
  imports: ResultsImports;
  fields: ResultsFieldMap;
}

/** Oeffentliche Sicht: alles ausser dem Schluessel. */
export interface ResultsConfig {
  source: ResultsSource;
  eventKey: string;
  intervalSeconds: number;
  imports: ResultsImports;
  fields: ResultsFieldMap;
  /** Letzte vier Zeichen des Schluessels -- genug zum Wiedererkennen. */
  keyHint: string;
  hasKey: boolean;
}

export const manualConfig = (): ResultsConfig => ({
  source: 'manual',
  eventKey: '',
  intervalSeconds: DEFAULT_RESULTS_INTERVAL,
  imports: defaultImports(),
  fields: emptyFieldMap(),
  keyHint: '',
  hasKey: false,
});

/* ------------------------------------------------------------- Zustand */

/**
 * `off` = von Hand, `live` = die Abfrage laeuft, `stale` = sie laeuft, aber
 * die letzte Antwort kam nicht durch, `error` = sie steht.
 */
export type ResultsPhase = 'off' | 'live' | 'stale' | 'error';

export const resultsPhaseLabel: Record<ResultsPhase, string> = {
  off: 'von Hand',
  live: 'holt Ergebnisse',
  stale: 'Antwort fehlt',
  error: 'Fehler',
};

export interface ResultsStatus {
  config: ResultsConfig;
  phase: ResultsPhase;
  /** Letzter Versuch, letzter Erfolg -- der Abstand zeigt ein Problem sofort. */
  lastPollAt: number | null;
  lastOkAt: number | null;
  /** Zeilen in der letzten Antwort. */
  lastRows: number;
  /** Uebernommene Aenderungen, seit die Quelle laeuft. */
  applied: number;
  /** Die letzte Antwort hatte eine Rundenspalte -- siehe `resultsBackyard`. */
  laps: boolean;
  message: string | null;
}

export const idleResults = (): ResultsStatus => ({
  config: manualConfig(),
  phase: 'off',
  lastPollAt: null,
  lastOkAt: null,
  lastRows: 0,
  applied: 0,
  laps: false,
  message: null,
});

/**
 * Was RACE RESULT beim Backyard fuehrt -- und was die Regie.
 *
 * - `field`: „im Rennen" kommt aus der Liste (alle ohne DNF), sobald sie
 *   einmal Zeilen gebracht hat.
 * - `loops`: die Loops auch, wenn die Liste Runden hat. Die Uhr zaehlt dann
 *   nicht mehr mit, sie sichert nur nach unten ab (`backyardFloorAtHour`).
 * - `outage`: RACE RESULT antwortet nicht mehr. Es gelten die letzten
 *   Zahlen, die Uhr zaehlt die Loops zur vollen Stunde, und die Regie darf
 *   wieder selbst zaehlen -- bis die naechste Antwort kommt.
 */
export interface ResultsBackyard {
  field: boolean;
  loops: boolean;
  outage: boolean;
}

export function resultsBackyard(status: ResultsStatus): ResultsBackyard {
  const field = status.config.source === 'raceresult' && status.lastRows > 0;
  return { field, loops: field && status.laps, outage: field && status.phase === 'stale' };
}

/**
 * Backyard-Zahlen aus einer Liste: im Rennen ist, wer kein DNF hat; ein
 * Loop ist geschafft, wenn *alle* im Rennen ihn geschafft haben -- die
 * kleinste Rundenzahl unter ihnen. Ist niemand mehr im Rennen, gilt die
 * groesste. `loops` ist null, wenn die Liste keine Runden hat.
 */
export function backyardCount(rows: ResultsRow[]): { runners: number; out: number; loops: number | null } {
  const active = rows.filter((r) => r.status !== 'dnf');
  const laps = (list: ResultsRow[]) => list.flatMap((r) => (r.laps === null ? [] : [r.laps]));
  const all = laps(rows);
  const running = laps(active);
  return {
    runners: active.length,
    out: rows.length - active.length,
    loops: running.length ? Math.min(...running) : all.length ? Math.max(...all) : null,
  };
}

/**
 * Gelten die Rundenzeiten aus RACE RESULT gerade fuer die Streckenkarte?
 *
 * Ja, solange die Abfrage laeuft und antwortet. Ein einzelner Aussetzer
 * zaehlt noch nicht -- sonst sprangen die Punkte bei jedem Wackler zwischen
 * zwei Prognosen hin und her. Erst wenn die letzte Antwort laenger als
 * `RESULTS_PACE_GRACE_MS` (mindestens drei Takte) her ist, rechnet die Karte
 * mit den eigenen Messungen der Regie weiter (`loopPrognosis`).
 */
export const RESULTS_PACE_GRACE_MS = 2 * 60_000;

export function resultsPaceLive(status: ResultsStatus, now: number = Date.now()): boolean {
  if (status.config.source !== 'raceresult' || status.lastOkAt === null) return false;
  if (status.phase === 'live') return true;
  if (status.phase !== 'stale') return false;
  const grace = Math.max(RESULTS_PACE_GRACE_MS, status.config.intervalSeconds * 3000);
  return now - status.lastOkAt < grace;
}

/* --------------------------------------------------------- Testabfrage */

/** Eine Zeile aus RACE RESULT, schon auf unsere Felder gelesen. */
export interface ResultsRow {
  bib: string;
  name: string;
  time: string;
  place: number | null;
  status: 'onCourse' | 'finished' | 'dnf';
  checkpoint: string;
  /** Geschaffte Runden, wenn die Liste sie hat -- sonst null. */
  laps: number | null;
  /** Durchschnittliche Rundenzeit in Sekunden („Runde ⌀"), wenn die Liste sie hat -- sonst null. */
  lapSeconds: number | null;
}

/**
 * Antwort der Testabfrage.
 *
 * Sie ist der Grund, warum diese Anbindung ohne Schluessel gebaut werden
 * kann: statt auf ein Antwortformat zu raten, zeigt sie beim ersten echten
 * Schluessel die erkannten Spalten und die ersten Zeilen roh daneben. Passt
 * die Zuordnung nicht, stellt die Regie sie in `fields` gerade -- ohne dass
 * jemand Code anfassen muss.
 */
export interface ResultsProbe {
  ok: boolean;
  message: string;
  /** Ueberschriften, wie RACE RESULT sie liefert. */
  columns: string[];
  /** Welche Spalte wir wofuer halten. */
  detected: Partial<Record<keyof ResultsFieldMap, string>>;
  /** Die ersten Zeilen, wie sie ankamen. */
  sample: unknown[];
  /** Dieselben Zeilen, wie wir sie lesen. */
  mapped: ResultsRow[];
  rows: number;
}
